/**
 * Safari Backdrop Blur Fix Utility
 * Detects Safari browser and applies fallbacks for backdrop-filter compatibility
 */

// Detect Safari browser
export const isSafari = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  return (
    userAgent.includes("safari") &&
    !userAgent.includes("chrome") &&
    !userAgent.includes("chromium") &&
    !userAgent.includes("edg")
  );
};

// Detect iOS device
export const isIOS = () => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

// Detect iPhone Safari specifically
export const isIPhoneSafari = () => {
  const userAgent = navigator.userAgent;
  return (
    /iPhone/.test(userAgent) &&
    /Safari/.test(userAgent) &&
    !/Chrome/.test(userAgent)
  );
};

// Detect Safari version
export const getSafariVersion = () => {
  if (!isSafari()) return null;

  const userAgent = navigator.userAgent;
  const match = userAgent.match(/version\/(\d+)/i);
  return match ? parseInt(match[1]) : null;
};

// Check if backdrop-filter is supported
export const supportsBackdropFilter = () => {
  return (
    CSS.supports("backdrop-filter", "blur(1px)") ||
    CSS.supports("-webkit-backdrop-filter", "blur(1px)")
  );
};

// iOS Safari specific modal fixes
export const applyIOSModalFixes = () => {
  if (!isIOS()) return;

  // Add iOS-specific body class
  document.body.classList.add("ios-device");

  // Fix viewport height for iOS Safari
  const setViewportHeight = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  };

  setViewportHeight();
  window.addEventListener("resize", setViewportHeight);
  window.addEventListener("orientationchange", setViewportHeight);

  // Prevent zoom on input focus
  const inputs = document.querySelectorAll("input, textarea, select");
  inputs.forEach((input) => {
    input.addEventListener("focus", () => {
      input.style.fontSize = "16px";
    });
  });
};

// Apply backdrop blur fallback to elements
export const applyBackdropBlurFallback = (
  selector,
  fallbackBg = "rgba(255, 255, 255, 0.9)"
) => {
  if (supportsBackdropFilter()) return; // No need for fallback

  const elements = document.querySelectorAll(selector);
  elements.forEach((element) => {
    // Remove backdrop-blur classes
    element.classList.remove(
      "backdrop-blur-sm",
      "backdrop-blur-md",
      "backdrop-blur-lg",
      "backdrop-blur-xl",
      "backdrop-blur-2xl",
      "backdrop-blur-3xl"
    );

    // Add fallback background
    element.style.backgroundColor = fallbackBg;
    element.style.boxShadow = "0 8px 32px rgba(0, 0, 0, 0.1)";
  });
};

// Initialize backdrop blur fallbacks for common elements
export const initBackdropBlurFallbacks = () => {
  if (supportsBackdropFilter()) return;

  // Apply fallbacks to common backdrop blur elements
  applyBackdropBlurFallback(".notification-container .notification");
  applyBackdropBlurFallback(
    ".deleted-bg, .deleted-cat-bg, .deleted-bg-pack",
    "rgba(0, 0, 0, 0.8)"
  );
  applyBackdropBlurFallback(".keyboard-shortcuts");

  // Apply to any element with backdrop-blur classes
  const backdropElements = document.querySelectorAll(
    '[class*="backdrop-blur"]'
  );
  backdropElements.forEach((element) => {
    element.style.backgroundColor = "rgba(255, 255, 255, 0.9)";
    element.style.boxShadow = "0 8px 32px rgba(0, 0, 0, 0.1)";
  });
};

// Enhanced backdrop blur with fallback
export const createBackdropBlurElement = (
  blurAmount = "md",
  fallbackBg = "rgba(255, 255, 255, 0.9)"
) => {
  const element = document.createElement("div");

  if (supportsBackdropFilter()) {
    element.style.webkitBackdropFilter = `blur(${
      blurAmount === "sm"
        ? "4px"
        : blurAmount === "md"
        ? "12px"
        : blurAmount === "lg"
        ? "16px"
        : blurAmount === "xl"
        ? "24px"
        : "12px"
    })`;
    element.style.backdropFilter = `blur(${
      blurAmount === "sm"
        ? "4px"
        : blurAmount === "md"
        ? "12px"
        : blurAmount === "lg"
        ? "16px"
        : blurAmount === "xl"
        ? "24px"
        : "12px"
    })`;
  } else {
    element.style.backgroundColor = fallbackBg;
    element.style.boxShadow = "0 8px 32px rgba(0, 0, 0, 0.1)";
  }

  return element;
};

// Auto-initialize when DOM is ready
if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initBackdropBlurFallbacks();
      applyIOSModalFixes();
    });
  } else {
    initBackdropBlurFallbacks();
    applyIOSModalFixes();
  }
}

export default {
  isSafari,
  isIOS,
  isIPhoneSafari,
  getSafariVersion,
  supportsBackdropFilter,
  applyBackdropBlurFallback,
  initBackdropBlurFallbacks,
  createBackdropBlurElement,
  applyIOSModalFixes,
};
